import type { ElementType, HTMLProps, ReactNode } from 'react';

import {
	type LayoutMode,
	type LayoutSpacing,
	type ResponsiveValue,
	toResponsiveValue,
	toSpacingValue,
	useContainerContext,
} from '@playbook/modules/layout/container/container-context';
import { type ClassName, cn, cv } from '@playbook/utils';

import css from './styles.module.css';

const DefaultLayoutGutter: LayoutSpacing = 10;

const columnLayoutClassNames: Record<LayoutMode, ClassName> = {
	container: css.columnContainer,
	viewport: css.columnViewport,
};

type VerticalAlign = 'start' | 'center' | 'end' | 'stretch';

interface ColumnProps extends Omit<HTMLProps<HTMLDivElement>, 'as' | 'rows'> {
	as?: ElementType;

	spacing?: ResponsiveValue<LayoutSpacing>;
	inline?: boolean;

	rows?: ResponsiveValue<string>;

	justify?: ResponsiveValue<VerticalAlign>;

	className?: string;

	children?: ReactNode | ReactNode[];
}

export function Column({
	as: Component = 'section',

	spacing = DefaultLayoutGutter,
	inline,

	rows = 'none',

	justify = 'stretch',

	className,
	style,
	children,

	...props
}: ColumnProps): ReactNode {
	const layout = useContainerContext();
	const inlineClassName = inline ? css.inline : null;

	const layoutClassName = layout ? columnLayoutClassNames[layout.mode] : null;

	const [defaultSpacing, mdSpacing, lgSpacing] = toResponsiveValue(spacing);
	const [defaultRows, mdRows, lgRows] = toResponsiveValue(rows);
	const [defaultJustify, mdJustify, lgJustify] = toResponsiveValue(justify);

	return (
		<Component
			{...props}
			className={cn(css.column, layoutClassName, inlineClassName, className)}
			style={{
				...style,
				...cv({
					spacing: toSpacingValue(defaultSpacing),
					spacingResponsive: toSpacingValue(mdSpacing),
					spacingResponsiveLg: toSpacingValue(lgSpacing),
					rows: defaultRows,
					rowsResponsive: mdRows,
					rowsResponsiveLg: lgRows,
					justify: defaultJustify,
					justifyResponsive: mdJustify,
					justifyResponsiveLg: lgJustify,
				}),
			}}>
			{children}
		</Component>
	);
}
